import type { ComponentType } from 'react';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface ReportCountTileProps {
  label: string;
  /** A count is formatted here; anything already worded is shown as given. */
  value: number | string;
  icon: ComponentType<{ className?: string }>;
}

/**
 * A small figure inside a report panel — one of a grid of counts, such as the
 * work done in the app or the tickets by status. A zero is drawn quiet, so the
 * figures that did move are the ones the eye lands on.
 */
export function ReportCountTile({ label, value, icon: Icon }: ReportCountTileProps) {
  const isZero = value === 0;

  return (
    <div className="flex items-center gap-3 rounded-field border border-line bg-surface-2 p-3">
      <span
        className="grid h-8 w-8 shrink-0 place-items-center rounded-field bg-surface text-accent"
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p
          className={cn(
            'text-lg font-[750] leading-none tabular-nums',
            isZero ? 'text-ink-3' : 'text-ink',
          )}
        >
          {typeof value === 'number' ? formatNumber(value) : value}
        </p>
        <p className="mt-1 truncate text-xs text-ink-3">{label}</p>
      </div>
    </div>
  );
}
